import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * A hook that provides hover state and event handlers
 * This is a compatibility layer between the old @hzdg/use-hover API
 * and the modern usehooks-ts useHover API
 *
 * @param options Configuration options
 * @returns [isHovering, hoverProps] - A tuple with hover state and event handlers
 */
export function useHoverEvents({
  mouseEnterDelayMS = 0,
  mouseLeaveDelayMS = 0,
} = {}): [boolean, { onMouseEnter: () => void; onMouseLeave: () => void }] {
  const [isHovering, setIsHovering] = useState(false);

  // Use refs to persist timer IDs between renders
  const mouseEnterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timers when component unmounts
  useEffect(() => {
    return () => {
      if (mouseEnterTimerRef.current) clearTimeout(mouseEnterTimerRef.current);
      if (mouseOutTimerRef.current) clearTimeout(mouseOutTimerRef.current);
    };
  }, []);

  const onMouseEnter = useCallback(() => {
    if (mouseOutTimerRef.current) {
      clearTimeout(mouseOutTimerRef.current);
      mouseOutTimerRef.current = null;
    }

    mouseEnterTimerRef.current = setTimeout(() => {
      setIsHovering(true);
    }, mouseEnterDelayMS);
  }, [mouseEnterDelayMS]);

  const onMouseLeave = useCallback(() => {
    if (mouseEnterTimerRef.current) {
      clearTimeout(mouseEnterTimerRef.current);
      mouseEnterTimerRef.current = null;
    }

    mouseOutTimerRef.current = setTimeout(() => {
      setIsHovering(false);
    }, mouseLeaveDelayMS);
  }, [mouseLeaveDelayMS]);

  return [isHovering, { onMouseEnter, onMouseLeave }];
}
